<template>
  <div v-if="storeAvailabilityContent.show && storeCount > 0">
    <template v-if="isTrigger">
      <div
        v-if="storeAvailabilityContent.show && showTrigger"
        class="store-availability store-availability--trigger
              text-desktop-small
              text-mobile-small"
      >
        <icon-map />

        <template v-if="variant.available && availableStores">
          <p
            class="
              store-availability__store
              store-availability__store--trigger
              text-desktop-small
              text-mobile-small
            "
            v-html="storeAvailabilityContent.useStorePageLinks ?
              formatStorePageLink(availableStores[0]?.message) :
              formatStorePageLink(availableStores[0]?.message)"
          />

          <button
            v-if="additionalStoresCount > 0"
            class="store-availability__stores-label text-desktop-small text-mobile-small"
            type="button"
            @click="() => handleTriggerClick()"
          >
            <span
              v-if="additionalStoresCount === 1"
              v-text="$string('product.store_availability.stores.one',
                              { replace: { count: 1 } }
              )"
            />

            <span
              v-else
              v-text="$string('product.store_availability.stores.other',
                              { replace: { count: (additionalStoresCount) } }
              )"
            />
          </button>
        </template>

        <button
          v-else
          type="button"
          class="store-availability__cta"
          @click="openOverlay({
            ignoreDismissed: true,
            namespace: 'storeAvailability',
          })"
          v-text="isMobile ?
            $string('product.store_availability.label_mobile') :
            $string('product.store_availability.select_size')"
        />
      </div>
    </template>

    <template v-else>
      <div
        v-if="product"
        class="store-availability__sizes"
      >
        <template
          v-for="(variantObject, index) of sortedSizes"
          :key="index"
        >
          <button
            class="
                text-desktop-small-uc size-selector__button
              "
            :class="{
              [$class('active')]: variantObject.id === selected?.id,
            }"
            type="button"
            @click="handleVariantUpdate(variantObject)"
            v-text="variantObject.title"
          />
        </template>
      </div>

      <div
        v-if="!variant"
        class="store-availability"
      >
        <div class="store-availability__content">
          <p
            class="text-desktop-small text-mobile-small"
            v-text="$string('product.store_availability.select_size')"
          />
        </div>
      </div>
      <div
        v-if="availableStores.length"
        class="store-availability"
      >
        <template v-if="availableStores[0].address?.countryCode === 'US'">
          <div
            v-for="(cityStores, city) of groupedStores(availableStores)"
            :key="city"
            class="store-availability__wrapper"
          >
            <h5 class="store-availability__city">
              {{ city }}
            </h5>
            <div
              v-for="store of cityStores"
              :key="store.address.address1"
              class="store-availability__store"
            >
              <icon-map />
              <p
                class="text-desktop-small text-mobile-small"
                v-html="storeAvailabilityContent.useStorePageLinks ?
                  formatStorePageLink(store?.message) :
                  store?.message"
              />
            </div>
          </div>
        </template>
        <div
          v-for="store of availableStores"
          v-else
          :key="`store-availability__store-${store.countryCode}`"
          class="store-availability__store"
        >
          <p
            class="text-desktop-small text-mobile-small"
            v-html="storeAvailabilityContent.useStorePageLinks ?
              formatStorePageLink(store?.message) :
              store?.message"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<script>

/**
 * Vue: store-availability (store-availability)
 * -----------------------------------------------------------------------------
 * Component that shows store availability for product variants.
 *
 * @param {Object} product - Shopify product object.
 * @param {Object|Boolean} variant - Canvas formatted variant object.
 * @param {Boolean} showStoreAvailability - Visible setting
 * @param {String} storesCount - Number of stores
 * @param {Boolean} isTrigger - Determines if this is shown as a CTA trigger
 */

import { mapActions, mapState } from 'vuex'
import IconMap from '~icons/misc/map.svg'
import { VariantsAvailability } from './variants-availability'

export default {
  name: 'StoreAvailability',

  components: {
    IconMap,
  },

  props: {
    product: {
      type: [Boolean, Object],
      default: false,
    },

    // eslint-disable-next-line vue/no-unused-properties
    storeAvailabilityContent: {
      type: Object,
      default: () => ({}),
    },

    sizeOrder: {
      type: Array,
      default: () => [],
    },

    isTrigger: {
      type: Boolean,
      default: false,
    },

    useStoreAvailabilityFromLocations: {
      type: Boolean,
      default: false,
    },

    coordinates: {
      type: Object,
      default: () => ({
        latitude: Number,
        longitude: Number,
      }),
    },

    variantAvailabilityData: {
      type: Array,
      default: () => [],
    },
  },

  emits: {
    'store-availability-action': (payload) => typeof payload === 'object',
  },

  data() {
    return {
      countryCode: '',
      selected: null,
      showTrigger: false,
    }
  },

  computed: {
    variantsAvailability() {
      if (!this.variant?.id) {
        return null
      }

      return new VariantsAvailability(this.variant.id, this.variantAvailabilityData)
    },

    storeCount() {
      if (this.useStoreAvailabilityFromLocations) {
        return this.availableStores?.length
      }

      return this.storeAvailabilityContent.storesCount
    },

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
      variant: (state) => state.product.variant,
    }),

    stores() {
      if (!this.countryCode || !this.variant) {
        return []
      }

      return this.variant?.physicalStockAvailability?.map(store => {
        let countryCode

        // Temporary solution until metafield is updated
        if (store.includes('Mount Street')) {
          countryCode = 'GB'
        } else if (store.includes('Biblioteksgatan') || store.includes('Nordiska')) {
          countryCode = 'SE'
        }

        return {
          message: store,
          countryCode,
        }
      })
    },

    /**
     * Compute countries from settings.
     * @returns {Array}
     */
    countriesToReturn() {
      return this.storeAvailabilityContent.countries?.split(',').map(country => country.trim())
    },

    /**
     * Compute visible stores from settings.
     * @returns {Array}
     */
    visibleStores() {
      return this.storeAvailabilityContent.visibleStores?.split(',')
    },

    /**
     * Compute available stores based on country code.
     * @returns {Array}
     */
    availableStores() {
      if (this.useStoreAvailabilityFromLocations) {
        if (this.coordinates) {
          // eslint-disable-next-line max-len
          const availableStoresFromLocationsSortedByProximity = this.variantsAvailability?.availableStoresFromLocationsSortedByProximity(this.coordinates?.latitude, this.coordinates?.longitude) || []
          return availableStoresFromLocationsSortedByProximity
        }

        return this.variantsAvailability?.availableStoresFromLocations()
      }

      // eslint-disable-next-line max-len
      return this.stores ? this.stores?.filter(store => store.countryCode === this.countryCode) && this.stores?.filter(store => this.visibleStores.includes(this.getStoreName(store?.message))) : false
    },

    additionalStoresCount() {
      if (this.useStoreAvailabilityFromLocations && this.availableStores?.length > 0) {
        return this.availableStores?.length - 1
      }

      return this.storeAvailabilityContent.additionalStoresCount
    },

    /**
     * Computes sorted product variants.
     * @returns {Array}
     */
    sortedSizes() {
      if (!this.product?.variants?.length) {
        return []
      }

      const newProduct = JSON.parse(JSON.stringify(this.product))

      if (!Number(newProduct.variants[0].title)) {
        return newProduct.variants.sort(
          (a, b) => {
            return this.sizeOrder?.indexOf(a.title) -
              this.sizeOrder?.indexOf(b.title)
          },
        )
      }

      return newProduct.variants.sort((a, b) => a.title - b.title)
    },
  },

  watch: {

    /**
     * When the variant updates we need to show the equivalent product variant
     * option as selected.
     */
    variant: {
      immediate: true,
      handler(variant) {
        if (!variant) {
          return
        }

        this.selected = variant
      },
    },
  },

  mounted() {
    if (cnvs?.store?.country && this.storeAvailabilityContent?.countries && this.isTrigger) {
      this.showTrigger = this.countriesToReturn.includes(cnvs?.store?.country)
    }

    if (cnvs?.store?.country) {
      this.countryCode = cnvs?.store?.country
    } else {
      this.getUserLocation()
    }
  },

  methods: {
    ...mapActions({
      closeOverlay: 'overlays/close',
      openOverlay: 'overlays/open',
    }),

    groupedStores(stores) {
      const grouped = {}
      stores.forEach((store) => {
        const { city } = store.address
        if (!grouped[city]) {
          grouped[city] = []
        }
        grouped[city].push(store)
      })
      return grouped
    },

    async getUserLocation() {
      const geoData = await fetch('/browsing_context_suggestions.json').then(data => data.json())

      this.countryCode = geoData?.detected_values?.country?.handle
    },

    formatStorePageLink(store) {
      if (store) {

        const storeName = this.getStoreName(store)
        const slugify = str => str
          .toString()
          .toLowerCase()
          .trim()
          .replace(/[^\w\s-]/g, '')
          .replace(/[\s_-]+/g, '-')
          .replace(/^-+|-+$/g, '')
          .replace('-store', '')

        const storeLink = `<a href="/pages/${slugify(storeName)}">${storeName}</a>`

        return storeName ? store.replace(storeName, storeLink) : ''
      }
      return ''

    },

    getStoreName(store) {
      const {
        notAvailable,
        limitedAvailability,
        available,
      } = window.cnvs.strings.product.store_availability

      if (store.includes(' at ')) {
        return store.split(' at ')[1]
      }
      if (store.includes(limitedAvailability)) {
        return store.split(limitedAvailability)[1]
      }
      if (store.includes(available)) {
        return store.split(available)[1]
      }

      if (store.includes(notAvailable)) {
        return store.split(notAvailable)[1]
      }

      return store
    },

    handleTriggerClick() {
      this.openOverlay({
        ignoreDismissed: true,
        namespace: 'storeAvailability',
      })

      this.handleGtm()
    },

    handleGtm() {
      const dataLayer = window.dataLayer || []

      dataLayer.push({
        event: 'store_inventory_click',
        storeInventoryClick: {
          url: document.location.pathname,
        },
      })
    },

    /**
     * When the column variant updates, emit an event to update the related
     * variant product.
     * @param {Number} index - Index of the selected variant.
     */
    handleVariantUpdate(variant) {
      if (!variant) {
        return
      }

      const event = {
        action: null,
        variant,
      }

      if (variant?.available) {
        event.action = 'variant-updated'
      } else {
        event.action = 'notify-me'
      }

      this.$emit('store-availability-action', event)
    },
  },
}
</script>

<style lang="scss">
@import './store-availability';
</style>
